import { useCallback, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from './language-context';
import { useSignedInAccount } from './signed-in-account';
import { surfaceUrl } from './surface-links';
import { AccountSheet, type AccountSheetMode } from './account-sheet';
import { CategoriesSheet } from './categories-sheet';

// Mobile bottom tab bar (design DC:3797-3808, tabs DC:4735-4748; §7.3).
//
// Five slots: Home · Offers · Categories · Bookings · More. Home and Offers navigate;
// Categories opens the categories sheet; Bookings and More open the account sheet
// (signed out) — a signed-in visitor's Bookings tab links straight to their dashboard.
// Navigating slots are real links and sheet slots are real buttons, so every slot is
// keyboard-reachable and announces what it does.
//
// Badges per design: Offers carries an always-on dot (live deals), Bookings a dot when
// a booking waits on the customer (payment verification / rejected proof), More the
// unread-notification count. Icons are the design's 24-viewbox stroke paths; the
// active icon thickens and pops (`popIn`, theme/motion.css).
//
// Hidden on the routes the design lists (business pages, the booking stepper, sign-in
// and sign-up) — those screens own the bottom edge with their own action bars — and on
// the Listings results page, whose design (DR-81) has no tab bar.

const HIDDEN_ON: RegExp[] = [/^\/(?:business|b|l)\//, /^\/search(?:\/|$)/, /^\/login\b/, /^\/signup\b/, /^\/merchant-signup\b/];

export function tabBarVisible(pathname: string): boolean {
  return !HIDDEN_ON.some((re) => re.test(pathname));
}

const ICONS = {
  home: 'M3.5 11 12 4l8.5 7M5.8 9.4V19a1 1 0 0 0 1 1h10.4a1 1 0 0 0 1-1V9.4M9.8 20v-5.4a1 1 0 0 1 1-1h2.4a1 1 0 0 1 1 1V20',
  offers: 'M12.6 2.6H5a2 2 0 0 0-2 2v7.6a2 2 0 0 0 .6 1.4l7.8 7.8a2 2 0 0 0 2.8 0l6.2-6.2a2 2 0 0 0 0-2.8L12.6 2.6ZM7.6 7.6h.01',
  cats: 'M4 5.2A1.2 1.2 0 0 1 5.2 4h4.2a1.2 1.2 0 0 1 1.2 1.2v4.2a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 9.4ZM13.4 5.2a1.2 1.2 0 0 1 1.2-1.2h4.2A1.2 1.2 0 0 1 20 5.2v4.2a1.2 1.2 0 0 1-1.2 1.2h-4.2a1.2 1.2 0 0 1-1.2-1.2ZM4 14.6a1.2 1.2 0 0 1 1.2-1.2h4.2a1.2 1.2 0 0 1 1.2 1.2v4.2A1.2 1.2 0 0 1 9.4 20H5.2A1.2 1.2 0 0 1 4 18.8ZM13.4 14.6a1.2 1.2 0 0 1 1.2-1.2h4.2a1.2 1.2 0 0 1 1.2 1.2v4.2a1.2 1.2 0 0 1-1.2 1.2h-4.2a1.2 1.2 0 0 1-1.2-1.2Z',
  bookings: 'M4 7a1.5 1.5 0 0 1 1.5-1.5h13A1.5 1.5 0 0 1 20 7v11.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5ZM4 10.6h16M8.2 3.2v4M15.8 3.2v4M8 14.4h4',
  more: 'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17ZM8.1 12h.01M12 12h.01M15.9 12h.01',
} as const;

function TabIcon({ path }: { path: string }) {
  return (
    <svg className="tabbar__icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

function TabBody({ icon, label, dot, count, dotLabel }: { icon: string; label: string; dot?: boolean; count?: string; dotLabel?: string }) {
  return (
    <>
      <TabIcon path={icon} />
      <span className="tabbar__label">{label}</span>
      {dot && <span className="tabbar__dot" aria-hidden="true" />}
      {dot && dotLabel && <span className="sr-only">({dotLabel})</span>}
      {count && <span className="tabbar__count">{count}</span>}
    </>
  );
}

export function TabBar() {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const { user, attention, unread } = useSignedInAccount();
  const [catsOpen, setCatsOpen] = useState(false);
  const [sheet, setSheet] = useState<AccountSheetMode | null>(null);
  // Stable closers: the sheets re-run their open effect (focus, scroll lock) when the
  // handler identity changes, so an inline arrow would refocus on every render.
  const closeCats = useCallback(() => setCatsOpen(false), []);
  const closeSheet = useCallback(() => setSheet(null), []);

  if (!tabBarVisible(pathname)) return null;

  const link = (to: string, key: MessageKey, icon: string, end: boolean, dot?: { label: MessageKey }) => (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'tabbar__item tabbar__item--active' : 'tabbar__item')}>
      {({ isActive }) => (
        <>
          <TabBody icon={icon} label={t(key)} dot={Boolean(dot)} dotLabel={dot ? t(dot.label) : undefined} />
          {/* The active tab is announced, not just coloured — colour alone is not an
              accessible state indicator. */}
          {isActive && <span className="sr-only">({t('common.selected')})</span>}
        </>
      )}
    </NavLink>
  );

  const bookingsHref = user ? surfaceUrl('app', '/bookings') : null;

  return (
    <>
      <nav className="tabbar" aria-label={t('tabs.label')}>
        {link('/', 'tabs.home', ICONS.home, true)}
        {link('/offer', 'tabs.offers', ICONS.offers, false, { label: 'tabs.offers_live' })}
        <button type="button" className="tabbar__item" aria-haspopup="dialog" aria-expanded={catsOpen} onClick={() => setCatsOpen(true)}>
          <TabBody icon={ICONS.cats} label={t('tabs.categories')} />
        </button>
        {bookingsHref ? (
          <a className="tabbar__item" href={bookingsHref}>
            <TabBody icon={ICONS.bookings} label={t('tabs.bookings')} dot={attention > 0} dotLabel={t('tabs.needs_attention')} />
          </a>
        ) : (
          <button
            type="button"
            className="tabbar__item"
            aria-haspopup="dialog"
            aria-expanded={sheet === 'bookings'}
            onClick={() => setSheet('bookings')}
          >
            <TabBody icon={ICONS.bookings} label={t('tabs.bookings')} dot={attention > 0} dotLabel={t('tabs.needs_attention')} />
          </button>
        )}
        <button
          type="button"
          className="tabbar__item"
          aria-haspopup="dialog"
          aria-expanded={sheet === 'more'}
          aria-label={unread > 0 ? `${t('tabs.more')} (${t('tabs.unread', { n: unread })})` : undefined}
          onClick={() => setSheet('more')}
        >
          <TabBody icon={ICONS.more} label={t('tabs.more')} count={unread > 0 ? formatNumber(lang, unread) : undefined} />
        </button>
      </nav>
      <CategoriesSheet open={catsOpen} onClose={closeCats} />
      <AccountSheet mode={sheet} onClose={closeSheet} />
    </>
  );
}
